import React from "react";
import "./DownloadCta.scss";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import SEO, { SITE_URL } from "../SEO/SEO";
import { CheckCircle2, QrCode, Smartphone, Wifi } from "lucide-react";

const APPLE_STORE_URL = "https://apps.apple.com/sg/app/teleport-now/id6756201717";
const ANDROID_STORE_URL = "https://play.google.com/store/apps/details?id=sg.teleportnow.app";
const DOWNLOAD_URL = "https://teleportapp.sg/download";
const QR_URL = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(DOWNLOAD_URL)}`;

const benefits = [
  "Browse eSIM coverage for local, regional, and global trips.",
  "Keep booking details, support, and receipts in your travel account.",
  "Review supported travel services and current information in the app.",
];

const trackStoreClick = (store) => {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", "app_store_click", {
      store,
      placement: "download_page",
    });
  }
};

const DownloadCta = () => {
  return (
    <>
      <SEO
        title="Download Teleport Now | Travel eSIMs, Deals & Trip Essentials"
        description="Download the Teleport Now app for travel eSIMs, booking management, app-only deals, rewards, and support before your next trip."
        canonicalPath="/download"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "SoftwareApplication",
              "@id": `${SITE_URL}/#app`,
              name: "Teleport Now",
              applicationCategory: "TravelApplication",
              operatingSystem: "iOS, Android",
              url: SITE_URL,
              downloadUrl: `${SITE_URL}/download`,
              sameAs: [APPLE_STORE_URL, ANDROID_STORE_URL],
            },
            {
              "@type": "WebPage",
              name: "Download Teleport Now",
              description:
                "Download the Teleport Now app for travel eSIMs, booking management, app-only deals, rewards, and support before your next trip.",
              url: `${SITE_URL}/download`,
              mainEntity: {
                "@id": `${SITE_URL}/#app`,
              },
            },
          ],
        }}
      />
      <Navbar />

      <main className="download-page">
        <section className="download-hero">
          <div className="download-copy">
            <p className="download-eyebrow">Your journey, in one place</p>
            <h1>Travel lighter with Teleport Now.</h1>
            <p className="download-subtitle">
              Search, book, stay connected, and keep every travel detail close.
              Teleport Now brings the essentials together before take-off and
              throughout your trip.
            </p>

            <div className="download-store-wrapper">
              <a
                href={APPLE_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackStoreClick("app_store")}
              >
                <img
                  className="store-badge apple"
                  src="/images/icons/appstore.svg"
                  alt="Download on App Store"
                />
              </a>

              <a
                href={ANDROID_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackStoreClick("google_play")}
              >
                <img
                  className="store-badge android"
                  src="/images/icons/googleplay.svg"
                  alt="Get it on Google Play"
                />
              </a>
            </div>

            <ul className="download-benefits" aria-label="App benefits">
              {benefits.map((benefit) => (
                <li key={benefit}>
                  <CheckCircle2 aria-hidden="true" />
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
          </div>

          <aside className="download-panel" aria-label="Scan to download">
            <div className="download-panel__heading">
              <QrCode aria-hidden="true" />
              <div>
                <strong>Open Teleport Now on your phone</strong>
                <span>Scan once, then choose your app store.</span>
              </div>
            </div>
            <div className="download-qr">
              <img src={QR_URL} alt="QR code for the Teleport Now download page" />
            </div>
            <div className="download-panel__note">
              <Smartphone aria-hidden="true" /> App Store and Google Play
              <span aria-hidden="true">•</span>
              <Wifi aria-hidden="true" /> Instant access
            </div>
          </aside>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default DownloadCta;
